import React from "react";
import ReactDOM from "react-dom";
import { FiX, FiCheck, FiKey, FiShield, FiZap, FiDollarSign } from "react-icons/fi";
import { createCheckoutSession } from "../../services/paymentsService";
import type { CreateCheckoutSessionResponse } from "../../types/payments";

// Type-safe icon wrappers
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;
const KeyIcon = FiKey as React.FC<React.SVGProps<SVGSVGElement>>;
const ShieldIcon = FiShield as React.FC<React.SVGProps<SVGSVGElement>>;
const ZapIcon = FiZap as React.FC<React.SVGProps<SVGSVGElement>>;
const DollarIcon = FiDollarSign as React.FC<React.SVGProps<SVGSVGElement>>;

interface ApiKeyUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const benefits = [
  {
    icon: ShieldIcon,
    title: "Complete Data Control",
    description:
      "Your API calls go directly to OpenAI or Anthropic. Your data never touches our servers for LLM processing.",
  },
  {
    icon: ZapIcon,
    title: "No Rate Limits",
    description:
      "Use your own API quotas and limits. No throttling or waiting in queues.",
  },
  {
    icon: DollarIcon,
    title: "Cost Transparency",
    description:
      "Pay only for what you use, directly to your LLM provider. Full visibility into your AI costs.",
  },
  {
    icon: KeyIcon,
    title: "Enterprise Security",
    description:
      "Ideal for organizations with strict data handling requirements and compliance needs.",
  },
];

/**
 * ApiKeyUpgradeModal - Paywall modal for custom API key feature
 * Shows marketing copy and prompts upgrade to Pro plan
 */
const ApiKeyUpgradeModal: React.FC<ApiKeyUpgradeModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    setError(null);

    try {
      // Store the current URL so we can redirect back after payment
      localStorage.setItem(
        "payment_return_url",
        window.location.pathname + window.location.search
      );

      const response: CreateCheckoutSessionResponse = await createCheckoutSession(
        "pro",
        "upgrade"
      );

      if (response.url) {
        window.location.href = response.url;
      } else {
        throw new Error("No checkout URL returned from server.");
      }
    } catch (err: any) {
      console.error("Payment checkout error", err);
      setError(err?.message || "An unexpected error occurred.");
      setLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="su-modal-overlay" onClick={handleBackdropClick}>
      <div className="su-modal" style={{ maxWidth: "560px" }}>
        {/* Header */}
        <div className="su-modal-header">
          <div className="su-modal-title">
            <KeyIcon style={{ width: 20, height: 20 }} />
            <span>UNLOCK CUSTOM API KEYS</span>
          </div>
          <button type="button" className="su-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="su-modal-body" style={{ padding: "24px" }}>
          {/* Pro Badge */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "16px",
            }}
          >
            <span
              style={{
                backgroundColor: "rgba(168, 85, 247, 0.2)",
                border: "1px solid #A855F7",
                color: "#A855F7",
                fontSize: "12px",
                fontWeight: "bold",
                padding: "4px 12px",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Pro Feature
            </span>
          </div>

          {/* Headline */}
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#FFFFFF",
              textAlign: "center",
              marginBottom: "8px",
            }}
          >
            Take Control of Your AI
          </h2>

          <p
            style={{
              color: "#9CA3AF",
              textAlign: "center",
              marginBottom: "24px",
              fontSize: "14px",
            }}
          >
            Use your own OpenAI or Anthropic API keys for maximum privacy,
            flexibility, and cost control.
          </p>

          {error && (
            <div
              style={{
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                border: "1px solid #EF4444",
                color: "#EF4444",
                padding: "12px",
                marginBottom: "16px",
                fontSize: "14px",
              }}
            >
              {error}
            </div>
          )}

          {/* Benefits Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: "#0a0a0a",
                    border: "1px solid #374151",
                    padding: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <Icon
                      style={{ width: 18, height: 18, color: "#A855F7" }}
                    />
                    <span
                      style={{
                        color: "#FFFFFF",
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      {benefit.title}
                    </span>
                  </div>
                  <p
                    style={{
                      color: "#9CA3AF",
                      fontSize: "12px",
                      lineHeight: "1.5",
                      margin: 0,
                    }}
                  >
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* What's Included */}
          <div
            style={{
              backgroundColor: "rgba(168, 85, 247, 0.1)",
              border: "1px solid rgba(168, 85, 247, 0.3)",
              padding: "16px",
              marginBottom: "24px",
            }}
          >
            <p
              style={{
                color: "#A855F7",
                fontWeight: "600",
                fontSize: "12px",
                textTransform: "uppercase",
                marginBottom: "12px",
              }}
            >
              Pro Plan Includes
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {[
                "Custom API Keys (OpenAI & Anthropic)",
                "Everything in Basic plan",
                "Data room access",
                "Deck storage & analytics",
                "Unlimited insights",
              ].map((feature, index) => (
                <li
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#D1D5DB",
                    fontSize: "13px",
                    marginBottom: index < 4 ? "8px" : 0,
                  }}
                >
                  <CheckIcon style={{ color: "#10B981", width: 14, height: 14 }} />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Pricing */}
          <div style={{ textAlign: "center", marginBottom: "16px" }}>
            <span
              style={{ fontSize: "36px", fontWeight: "bold", color: "#FFFFFF" }}
            >
              $75
            </span>
            <span style={{ fontSize: "16px", color: "#9CA3AF" }}>/month</span>
          </div>

          {/* CTA Button */}
          <button
            type="button"
            onClick={handleUpgrade}
            disabled={loading}
            style={{
              width: "100%",
              padding: "14px",
              backgroundColor: "#A855F7",
              color: "#FFFFFF",
              border: "none",
              fontWeight: "600",
              fontSize: "14px",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
              transition: "all 0.2s ease",
            }}
          >
            {loading ? "Processing..." : "UPGRADE TO PRO"}
          </button>
        </div>

        {/* Footer */}
        <div className="su-modal-footer">
          <button
            type="button"
            className="su-btn-secondary"
            onClick={onClose}
            style={{ width: "100%" }}
          >
            MAYBE LATER
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ApiKeyUpgradeModal;
